/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, useRef } from 'react';
import { useLocation, useMatches } from 'react-router';

import { nameDatadogView, setDatadogPageType } from '@/components/datadog/datadog-rum.utils';

import type { RouteAnalytics } from './data-layer';

/** A loader whose data carries a `RouteAnalytics` object under `analytics`. */
type MatchWithAnalytics = { data?: { analytics?: RouteAnalytics } };

/**
 * Names each Datadog RUM view after the route's own `analytics.page.pageName` (UCR-3022).
 *
 * Left alone, RUM derives view names from the URL, so every PDP is its own view and the Views list
 * degrades into thousands of one-hit rows that no dashboard can group. The loaders already produce
 * a stable `site:section:subsection` page name for the Adobe data layer, which is exactly the
 * grouping we want here — so this reuses it rather than inventing a second naming scheme that
 * would drift from it.
 *
 * Deliberately a sibling of `<DataLayerTracker>` rather than a few extra lines inside it: the two
 * have independent failure modes (a blocked RUM agent must not stop an Adobe push, and vice versa)
 * and independent vendors. Same shape though — client-only, reads the leaf match, deduped per path
 * so a StrictMode remount doesn't start the same view twice.
 *
 * Routes that return no `analytics` push nothing, and RUM keeps its URL-derived name for those.
 */
export function DatadogViewTracker() {
    const location = useLocation();
    const matches = useMatches();
    const lastViewRef = useRef<string | null>(null);

    // The leaf match is the most specific route; its loader carries the page's analytics payload.
    const analytics = (matches.at(-1) as MatchWithAnalytics | undefined)?.data?.analytics;
    const pageName = analytics?.page?.pageName;
    const pageType = analytics?.page?.pageType;

    useEffect(() => {
        if (typeof window === 'undefined') return;

        // Dedup on the resolved URL, matching the agent: it opens a view per URL change, so acting
        // once per URL covers each of them exactly once. A re-render of the same URL does nothing.
        //
        // Re-renders only — a StrictMode remount reinitialises the ref and this runs again. Left
        // that way on purpose: both calls below are idempotent, so a duplicate costs one wasted
        // call, and hoisting to module scope to prevent it would buy a test-only reset hook.
        const fullPath = `${location.pathname}${location.search}`;
        if (lastViewRef.current === fullPath) return;
        lastViewRef.current = fullPath;

        // Runs on EVERY navigation, including routes that expose no analytics payload — cart,
        // checkout and account do not. `pageType` lands in Datadog's *global* context, so skipping
        // those routes would leave the previous page's value attached: navigate PDP → checkout and
        // every checkout error would report `pageType: product`. `unknown` is the payload's own
        // documented default for "no better answer".
        setDatadogPageType(pageType ?? 'unknown');

        // The name is only ours to set when the route actually supplies one. Routes without a
        // payload keep the agent's URL-derived name, which is a worse name but an honest one.
        if (pageName) nameDatadogView(pageName);
    }, [pageName, pageType, location.pathname, location.search]);

    return null;
}
